/* Bottom-corner actions: back-to-top, and the live chat facade.

   Chat: Social Intents normally loads on every page (its loader, jQuery 3.6
   from Google's CDN, Font Awesome 4.6, a CSS file it fetches cross-origin and
   a 76KB widget) and pins a 250px tab over the page. Here nothing loads until
   a visitor presses the launcher. Then the vendor script is injected, and once
   it calls window.onSIApiReady the chat window opens through its SI_API. The
   vendor tab stays hidden (see .psj-chat-facade in floating-actions.liquid),
   so this launcher is the only way in and out.

   Loading on click rather than on idle keeps the cost at zero for everyone who
   never chats. The trade-off is that operators only see a visitor once they
   open the chat, which suits this account: it runs in "leave a message" mode.
   Once a visitor has opened the chat, later pages in the same session load it
   in the background (without opening it) so a conversation carries over. */
(() => {
  'use strict';

  const SESSION_KEY = 'psj-chat-opened';
  const LOAD_TIMEOUT = 12000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Back to top ---------- */
  const initBackToTop = () => {
    const button = document.querySelector('[data-back-to-top]');
    if (!button) return;

    let frame = 0;
    const update = () => {
      button.hidden = window.scrollY < window.innerHeight * 2;
    };
    window.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();

    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      // Put keyboard focus back at the top of the page too. The skip link is
      // the first focusable element and shows itself when focused by keyboard.
      const target = document.querySelector('.skip-to-content') || document.body;
      target.focus({ preventScroll: true });
    });
  };

  /* ---------- Live chat facade ---------- */
  const initChat = () => {
    const launcher = document.querySelector('[data-chat-launcher]');
    if (!launcher) return;

    const { widgetId, fallbackUrl, labelOpen, labelClose, labelLoading } = launcher.dataset;
    const badge = launcher.querySelector('.chat-launcher__badge');
    let loading = null;
    let observing = false;

    // The visible text is a single word ("Chat"); the full name lives in aria-label
    const setLabel = (text) => {
      if (text) launcher.setAttribute('aria-label', text);
    };

    const widget = () => document.getElementById('siWidget-chat');
    const isOpen = () => {
      const el = widget();
      return Boolean(el) && getComputedStyle(el).display !== 'none';
    };

    const syncState = () => {
      const open = isOpen();
      launcher.setAttribute('aria-expanded', String(open));
      setLabel(open ? labelClose : labelOpen);
      return open;
    };

    // Follow the vendor window: when it closes itself (its own minimise
    // button), update the launcher and bring focus back from the iframe.
    const observe = () => {
      if (observing) return;
      const el = widget();
      if (!el) return;
      observing = true;
      let wasOpen = isOpen();
      new MutationObserver(() => {
        const open = syncState();
        if (wasOpen && !open) {
          const active = document.activeElement;
          if (!active || active === document.body || active.tagName === 'IFRAME' || el.contains(active)) {
            launcher.focus({ preventScroll: true });
          }
        }
        wasOpen = open;
      }).observe(el, { attributes: true, attributeFilter: ['class', 'style'] });

      // Unread messages: the vendor counts them on the tab we hide, so mirror a dot here
      const notify = document.querySelector('#social-intents-tab-chat .siNotify');
      if (notify && badge) {
        const syncBadge = () => {
          badge.hidden = getComputedStyle(notify).display === 'none' || !notify.textContent.trim();
        };
        new MutationObserver(syncBadge).observe(notify, { attributes: true, childList: true, characterData: true, subtree: true });
      }
    };

    const preconnect = () => {
      if (preconnect.done) return;
      preconnect.done = true;
      ['https://www.socialintents.com', 'https://ajax.googleapis.com'].forEach((href) => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = '';
        document.head.appendChild(link);
      });
    };

    const load = () => {
      if (window.SI_API) return Promise.resolve();
      if (loading) return loading;

      loading = new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Social Intents did not load')), LOAD_TIMEOUT);
        const previous = window.onSIApiReady;
        window.onSIApiReady = () => {
          clearTimeout(timer);
          if (typeof previous === 'function') previous();
          resolve();
        };
        document.documentElement.classList.add('psj-chat-facade');
        const script = document.createElement('script');
        // The loader reads the account id from the hash of its own src
        script.src = `https://www.socialintents.com/api/socialintents.1.3.js#${widgetId}`;
        script.async = true;
        script.onerror = () => {
          clearTimeout(timer);
          reject(new Error('Social Intents failed to load'));
        };
        document.body.appendChild(script);
      });
      return loading;
    };

    // Once per page. hideTab() also closes the chat window, so it must never
    // run while the window is open.
    let prepared = false;
    const ready = () => {
      if (prepared) return;
      prepared = true;
      try {
        window.SI_API.hideTab();
      } catch (error) {
        /* The CSS rule keeps the tab hidden regardless */
      }
      observe();
    };

    launcher.addEventListener('pointerenter', preconnect, { once: true });
    launcher.addEventListener('focus', preconnect, { once: true });

    launcher.addEventListener('click', () => {
      if (launcher.getAttribute('aria-busy') === 'true') return;

      if (window.SI_API) {
        const open = isOpen();
        ready();
        if (open) window.SI_API.hidePopup();
        else window.SI_API.showPopup();
        syncState();
        return;
      }

      launcher.setAttribute('aria-busy', 'true');
      setLabel(labelLoading);
      load()
        .then(() => {
          ready();
          window.SI_API.showPopup();
          try {
            sessionStorage.setItem(SESSION_KEY, '1');
          } catch (error) {
            /* Private mode: the chat just won't preload on the next page */
          }
        })
        .catch(() => {
          // Blocked or down (ad blockers often block it): the contact page still works
          if (fallbackUrl) window.location.href = fallbackUrl;
        })
        .finally(() => {
          launcher.removeAttribute('aria-busy');
          // The window animates in; read its state once it has settled
          setTimeout(syncState, 400);
          syncState();
        });
    });

    // A chat opened earlier this session: load quietly so it carries over
    let opened = false;
    try {
      opened = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      opened = false;
    }
    if (opened) {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 2000));
      idle(() => load().then(ready).catch(() => {}), { timeout: 4000 });
    }
  };

  const init = () => {
    initBackToTop();
    initChat();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
